import { Transaction } from 'prosemirror-state'
import { Node } from 'prosemirror-model'
import { TrackedChangeType, TrackedChange } from 'prose-core'
import dayjs from 'dayjs'
import { ourUuid } from 'utils'
import { Context } from './Types'

export function addInsertionMarks(tr: Transaction, from: number, to: number, userId: number, context?: Context) {
  const schema = tr.doc.type.schema
  const resPosFrom = tr.doc.resolve(tr.selection.from)
  const siblingInsertion = findSiblingInsertion(tr, from)
  const parentTrackedChange = (resPosFrom.node().attrs.trackedChanges || []).find(
    ({ type }: TrackedChange) => type === TrackedChangeType.Insertion,
  )
  const createdAt = siblingInsertion?.createdAt || dayjs().toISOString()
  const newUuid = ourUuid()

  tr.doc.nodesBetween(from, to, (node, pos) => {
    const start = Math.max(from, pos)
    const end = Math.min(pos + node.nodeSize, to)

    /*
     * Smart links are inline nodes which also contain text. Because the text
     * inside is not actually editable (we force the content when we create the
     * node), we only track the smart link as a node, and not descend into it.
     */
    if (node.type === schema.nodes.smartLink) {
      /*
       * When inserting a smart link, the selection is at this point *after*
       * the smart link, so we need to peek at the text node before the smart
       * link looking for an existin insertion mark.
       */
      const markBefore = tr.doc.resolve(pos).nodeBefore?.marks.find((mark) => mark.type === schema.marks.insertion)
      addInsertionChange(tr, node, pos, {
        type: TrackedChangeType.Insertion,
        userId,
        createdAt,
        uuid: context?.uuid || parentTrackedChange?.uuid || markBefore?.attrs.uuid || newUuid,
      })
      return false
    }

    if (node.isInline) {
      const uuid = context?.uuid || parentTrackedChange?.uuid || siblingInsertion?.uuid || newUuid
      const mark = schema.marks.insertion.create({ userId, createdAt, uuid })

      tr.removeMark(start, end, schema.marks.deletion)
      tr.removeMark(start, end, schema.marks.insertion)
      tr.addMark(start, end, mark)

      return false
    }

    if (node.type.spec.attrs?.trackedChanges) {
      return addInsertionChange(tr, node, pos, {
        type: TrackedChangeType.Insertion,
        userId,
        createdAt,
        uuid: context?.uuid || parentTrackedChange?.uuid || newUuid,
      })
    }
  })
}

export const addInsertionChange = (tr: Transaction, node: Node, pos: number, trackedChange: TrackedChange) => {
  const trackedChanges = (node.attrs.trackedChanges || [])
    .filter(({ type }: TrackedChange) => ![TrackedChangeType.Insertion, TrackedChangeType.Deletion].includes(type))
    .concat(trackedChange)
  /*
   * TODO: Types for this method are not expecting null as a second argument. We
   * should fork the types definitions and update them to match our version
   * of prosemirror-transform.
   */
  tr.setNodeMarkup(
    pos,
    // @ts-ignore
    null,
    {
      ...node.attrs,
      trackedChanges,
    },
    node.marks,
  )

  return true
}

/**
 * Find the attributes of a preceding insertion mark, if present
 * @param tr The current transaction
 * @param pos The position from where to look for a sibling insertion
 * @returns The attributes of the insertion mark of a sibling text node, if present
 */
const findSiblingInsertion = (tr: Transaction, pos: number) => {
  const nodeBefore = tr.doc.resolve(pos).nodeBefore
  if (!nodeBefore) {
    return null
  }

  const schema = tr.doc.type.schema
  if (nodeBefore.type === schema.nodes.smartLink) {
    return nodeBefore.attrs.trackedChanges?.find(({ type }: TrackedChange) => type === TrackedChangeType.Insertion)
  }

  return nodeBefore.marks.find((mark) => mark.type === schema.marks.insertion)?.attrs
}
